<template>
  <v-dialog :value="value" persistent fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card dark tile class="policy-card d-flex flex-column">
      <!-- HEADER -->
      <div class="policy-header">
        <div class="policy-header-bg"></div>
        <div class="policy-header-overlay"></div>
        <div class="policy-header-content d-flex flex-column align-center justify-center">
          <div class="policy-icon-wrapper mb-4">
            <v-icon size="56" color="white">mdi-shield-check-outline</v-icon>
          </div>
          <h1 class="text-h5 font-weight-bold white--text text-center px-6" style="text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5)">
            Nutzungs- und Datenschutzreglement
          </h1>
          <p class="subtitle-2 mt-2 blue-grey--text text--lighten-3 text-center px-8" style="max-width: 400px; line-height: 1.5">
            Bitte lies das folgende Reglement sorgfältig durch und bestätige es mit deiner Unterschrift.
          </p>
        </div>
      </div>

      <!-- SCROLLABLE CONTENT -->
      <div class="policy-scroll-area flex-grow-1" ref="scrollArea" @scroll="handleScroll">
        <div class="px-5 py-6">
          <div class="policy-section" v-for="(section, i) in sections" :key="i">
            <div class="d-flex align-start mb-2">
              <v-avatar size="28" color="rgba(33, 150, 243, 0.15)" class="mr-3 mt-1 flex-shrink-0">
                <span class="blue--text text--lighten-2 caption font-weight-bold">{{ i + 1 }}</span>
              </v-avatar>
              <h3 class="text-subtitle-1 font-weight-bold white--text" style="line-height: 1.4">{{ section.title }}</h3>
            </div>
            <div class="pl-10">
              <p v-for="(point, j) in section.points" :key="j" class="body-2 grey--text text--lighten-1 mb-2" style="line-height: 1.6">
                {{ point }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="policy-footer px-5 py-4">
        <v-checkbox v-model="accepted" dark hide-details class="mt-0 mb-3" color="blue lighten-1">
          <template v-slot:label>
            <span class="body-2 white--text" style="line-height: 1.4">
              Ich habe das Reglement gelesen und verstanden und verpflichte mich, die Vorgaben einzuhalten.
            </span>
          </template>
        </v-checkbox>

        <v-btn
          block
          large
          rounded
          depressed
          color="blue darken-1"
          :disabled="!accepted"
          :loading="isSubmitting"
          class="font-weight-bold text-none policy-accept-btn"
          @click="submitAcceptance">
          <v-icon left>mdi-check-decagram</v-icon>
          Akzeptieren &amp; Fortfahren
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import chatApi from "@/api"

export default {
  name: "ChatPolicyDialog",
  props: {
    value: { type: Boolean, default: true },
  },
  data() {
    return {
      accepted: false,
      isSubmitting: false,
      hasScrolledToBottom: false,
      sections: [
        {
          title: "Zweck und Geltungsbereich",
          points: [
            "Dieses Reglement regelt die Nutzung des internen Mitarbeiter-Chats innerhalb der App von well come FIT durch Mitarbeitende sowie weitere autorisierte Personen.",
            "Der Chat dient der internen Organisation und Kommunikation, insbesondere der Abstimmung von Abläufen, der internen Information und der operativen Zusammenarbeit.",
            "Dieses Reglement ergänzt bestehende Weisungen zu Datenschutz, IT-Sicherheit, Geheimhaltung und arbeitsvertraglichen Pflichten.",
          ],
        },
        {
          title: "Bearbeitete Daten",
          points: [
            "Im Rahmen des Chats können insbesondere folgende Daten bearbeitet werden: Informationen, Verträge, interne News, Kampagnen, Arbeitszeiten, Löhne, Benutzer- und Rollendaten, technische Nutzungs- und Protokolldaten sowie Anhänge.",
            "Soweit im Einzelfall besonders schützenswerte Personendaten oder andere heikle Informationen bearbeitet werden, ist besondere Zurückhaltung zu wahren; es dürfen nur jene Angaben bearbeitet werden, die für den konkreten Zweck erforderlich sind.",
          ],
        },
        {
          title: "Zulässige Zwecke",
          points: [
            "Die Bearbeitung der Daten erfolgt ausschliesslich für betriebliche Zwecke, namentlich für Organisation, Kommunikation, Koordination, Dokumentation, Sicherheit, Support, Nachvollziehbarkeit und die Erfüllung gesetzlicher Pflichten.",
            "Eine Nutzung zu privaten, sachfremden oder nicht freigegebenen Zwecken ist unzulässig.",
          ],
        },
        {
          title: "Zugriffs- und Rollenprinzip",
          points: [
            "Die App steht Mitarbeitenden zur Verfügung. Der Zugang zu konkreten Chatinhalten richtet sich nach den jeweils freigegebenen Gruppen, Kanälen, Rollen und Funktionen.",
            "Auch wenn die App unternehmensweit verwendet wird, gilt innerhalb der Anwendung das Need-to-know-Prinzip. Inhalte dürfen nur denjenigen Personen zugänglich sein, die sie für ihre Tätigkeit benötigen.",
            "Berechtigungen sind bei Eintritt, Rollenwechsel und Austritt unverzüglich anzupassen.",
          ],
        },
        {
          title: "Verhaltenspflichten der Mitarbeitenden",
          points: [
            "Mitarbeitende behandeln sämtliche Chatinhalte vertraulich und geben diese nicht an unberechtigte Dritte weiter.",
            "Vor dem Versand sind Empfänger, Inhalte und Anhänge sorgfältig zu prüfen.",
            "Screenshots, Exporte, Weiterleitungen oder Kopien ausserhalb der vorgesehenen betrieblichen Prozesse sind untersagt.",
            "Zugangsdaten sind geheim zu halten; Endgeräte sind mit angemessenen Schutzmassnahmen zu sichern.",
          ],
        },
        {
          title: "Zugriff auf Daten durch interne Stellen",
          points: [
            "Ein routinemässiges Lesen von Chatinhalten durch Vorgesetzte, Administratoren, Systembetreuer oder Entwickler ist nicht zulässig.",
            "Ein Zugriff auf Inhalte oder Anhänge ist nur ausnahmsweise zulässig, wenn dies für einen klar bestimmten Zweck erforderlich ist, etwa zur Störungsbehebung, Untersuchung eines Sicherheits- oder Datenschutzvorfalls, Wiederherstellung von Daten, Abklärung eines konkreten Missbrauchsverdachts oder Erfüllung gesetzlicher Pflichten.",
            "Ein solcher Zugriff hat zweckgebunden, dokumentiert, protokolliert und auf das minimal Notwendige beschränkt zu erfolgen.",
          ],
        },
        {
          title: "Externe Dienstleister und Auslandzugriffe",
          points: [
            "Für die vorliegende Fassung sind keine externen Dienstleister vorgesehen.",
            "Auslandzugriffe sind nicht vorgesehen. Änderungen dieser Ausgangslage bedürfen einer vorgängigen rechtlichen und organisatorischen Prüfung sowie einer Aktualisierung der Information an die Mitarbeitenden.",
          ],
        },
        {
          title: "Protokollierung und Sicherheit",
          points: [
            "Sicherheitsrelevante und administrative Handlungen dürfen protokolliert werden, soweit dies für den sicheren Betrieb, die Missbrauchsverhinderung, die Nachvollziehbarkeit und die Störungsbehebung erforderlich ist.",
            "Eine dauernde persönliche Verhaltens- oder Leistungskontrolle der Mitarbeitenden ist nicht Zweck dieses Systems.",
            "Technische und organisatorische Massnahmen sind nach dem Risiko auszulegen; insbesondere sind Berechtigungsmanagement, Zugriffsschutz, Protokollierung und sichere Geräteverwaltung sicherzustellen.",
          ],
        },
        {
          title: "Aufbewahrung und Löschung",
          points: [
            "Chatdaten und zugehörige Systemdaten werden grundsätzlich 5 Jahre aufbewahrt, sofern keine längere gesetzliche Aufbewahrungspflicht, kein hängiger Streitfall und kein dokumentierter Sicherheits- oder Compliance-Vorfall eine längere Sicherung erfordern.",
            "Nach Ablauf der Aufbewahrungsfrist sind die Daten zu löschen, zu anonymisieren oder archivrechtlich zulässig auszusondern.",
            "Mitarbeitende können Anliegen betreffend Auskunft, Berichtigung oder Löschung an ihre oder ihren Vorgesetzten richten. Dieser koordiniert die Prüfung mit der zuständigen internen Stelle.",
          ],
        },
        {
          title: "Meldung von Vorfällen",
          points: [
            "Fehlversände, Fehlfreigaben, verdächtige Zugriffe, Verlust von Geräten oder sonstige Datenschutz- und Sicherheitsvorfälle sind unverzüglich dem oder der Vorgesetzten zu melden.",
            "Die Meldung hat vollständig und ohne Verzögerung zu erfolgen, damit Schutz- und Abhilfemassnahmen eingeleitet werden können.",
          ],
        },
        {
          title: "Verstösse",
          points: [
            "Verstösse gegen dieses Reglement können zu organisatorischen, arbeitsrechtlichen, zivilrechtlichen oder strafrechtlichen Konsequenzen führen.",
            "Das Unternehmen kann Zugriffsrechte einschränken oder entziehen, soweit dies zur Risikobegrenzung erforderlich ist.",
          ],
        },
        {
          title: "Inkrafttreten und Bestätigung",
          points: [
            "Dieses Reglement tritt mit Freigabe durch well come FIT in Kraft.",
            "Die Mitarbeitenden bestätigen mit ihrer Unterschrift, dass sie dieses Reglement erhalten, gelesen und verstanden haben und die Vorgaben einhalten.",
          ],
        },
      ],
    }
  },
  methods: {
    handleScroll() {
      const el = this.$refs.scrollArea
      if (!el) return
      const tolerance = 50
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - tolerance) {
        this.hasScrolledToBottom = true
      }
    },
    async submitAcceptance() {
      this.isSubmitting = true
      try {
        const { data } = await chatApi.acceptChatPolicy()

        // User in localStorage aktualisieren
        if (data && data.user) {
          const stored = JSON.parse(localStorage.getItem("user") || "{}")
          stored.user = { ...stored.user, chatPolicyAcceptedAt: data.user.chatPolicyAcceptedAt }
          localStorage.setItem("user", JSON.stringify(stored))
        }

        this.$emit("accepted")
        this.$emit("input", false)
      } catch (err) {
        console.error("Fehler beim Akzeptieren:", err)
        if (this.$toast) {
          this.$toast.error("Fehler beim Speichern. Bitte versuche es erneut.")
        }
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.policy-card {
  background-color: #0d0d0d !important;
  height: 100vh;
  overflow: hidden;
}

/* HEADER */
.policy-header {
  position: relative;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.policy-header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0d47a1 0%, #1565c0 30%, #1976d2 60%, #1e88e5 100%);
  z-index: 0;
}
.policy-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(13, 13, 13, 0.85) 90%, #0d0d0d 100%);
  z-index: 1;
}
.policy-header-content {
  position: relative;
  z-index: 2;
  padding-top: env(safe-area-inset-top);
}
.policy-icon-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4), 0 0 50px rgba(33, 150, 243, 0.2);
}

/* SCROLLABLE CONTENT */
.policy-scroll-area {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.policy-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.policy-section:last-child {
  border-bottom: none;
}

/* FOOTER */
.policy-footer {
  background: rgba(13, 13, 13, 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  padding-bottom: calc(16px + env(safe-area-inset-bottom)) !important;
}
.policy-accept-btn {
  letter-spacing: 0.5px;
  font-size: 15px !important;
  height: 48px !important;
}
.policy-accept-btn.v-btn--disabled {
  opacity: 0.4 !important;
}

/* Checkbox style override */
::v-deep .v-input--checkbox .v-input__slot {
  align-items: flex-start !important;
}
::v-deep .v-input--checkbox .v-icon {
  margin-top: 2px;
}
</style>
